import { Link } from "react-router-dom";
import styles from "./AdminShared.module.css";
import { utmVocabulary } from "../../../data/utmTracking";

const AdminHelp = () => {
  return (
    <div>
      <div className={styles["page-header"]}>
        <div className={styles["page-header-content"]}>
          <h1 className={styles["page-title"]}>Admin help</h1>
          <p className={styles["page-subtitle"]}>
            Quick reference for catalog, content, drafts, and preview.
          </p>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles["help-section"]}>
          <h3>Getting around</h3>
          <ul>
            <li>
              <strong>Dashboard</strong> — counts, type breakdown, recent edits.
            </li>
            <li>
              <strong>Catalog</strong> — songs, albums, playlists, streaming
              services.
            </li>
            <li>
              <strong>Content</strong> — notices, about, social links, menu
              order, join splash, join invite.
            </li>
            <li>
              <strong>Metadata</strong> — aggregated values (services, tags,
              etc.).
            </li>
            <li>
              <strong>Tracking</strong> — UTM landings, founding-list joins,
              link builder, JSON export.
            </li>
            <li>
              <strong>Tools</strong> — refresh data, export JSON.
            </li>
          </ul>
        </div>

        <div className={styles["help-section"]}>
          <h3>Rich text editors</h3>
          <ul>
            <li>Used for content fields such as text, textTwo, textThree, textFour.</li>
            <li>
              Because the site uses a special process for gradient text, standard
              text color often will not stick. Use the{" "}
              <strong>Background Color</strong> control to set text color.
            </li>
            <li>
              The first colors in the picker are theme colors.
            </li>
          </ul>
        </div>

        <div className={styles["help-section"]}>
          <h3>Menu order</h3>
          <ul>
            <li>
              Create or edit a content item with type <code>menuOrder</code>.
            </li>
            <li>
              Put the order in the <code>menuOrder</code> field as a
              comma-separated list.
            </li>
            <li>
              Example format:{" "}
              <code>noticeOne, noticeThree, about, noticeTwo, music</code>
            </li>
          </ul>
        </div>

        <div className={styles["help-section"]}>
          <h3>Pre-release / upcoming drops</h3>
          <p className={styles["help-lead"] || undefined}>
            Catalog list shows an amber <strong>Upcoming</strong> badge when{" "}
            <code>releaseDate</code> is after today. Filter with{" "}
            <strong>Upcoming only</strong>. Edit/create forms show the same
            status next to the release date field.
          </p>
          <ul>
            <li>
              Set a catalog item <strong>active</strong> with a future{" "}
              <code>releaseDate</code> to show the public upcoming-drop teaser
              instead of the player.
            </li>
            <li>
              Optional <strong>Pre-release message</strong> is the custom hype
              line under the title. Leave it blank to use the default.
            </li>
          </ul>
        </div>

        <div className={styles["help-section"]}>
          <h3>Catalog types</h3>
          <ul>
            <li>
              <code>song</code>, <code>album</code>, <code>playlist</code> —
              music items with streaming URLs, release info, tags, and about
              copy. Create/edit forms only show these fields for music types.
            </li>
            <li>
              <code>service</code> — streaming platform (slug, iframe
              attributes, optional <code>defaultEmbedQuery</code>). Forms hide
              music-only fields and show embed settings instead. Active
              services appear as URL columns on music items.
            </li>
            <li>
              Changing the <strong>Type</strong> dropdown updates which fields
              appear. Flags like <code>isDefaultPlaylist</code>,{" "}
              <code>isFeaturedPlaylist</code>, and <code>isDefaultService</code>{" "}
              control home-page defaults.
            </li>
          </ul>
        </div>

        <div className={styles["help-section"]}>
          <h3>Content types</h3>
          <ul>
            <li>
              <code>about</code>, <code>noticeOne</code>, <code>noticeTwo</code>
              , <code>noticeThree</code> — public site sections.
            </li>
            <li>
              <code>socialConnection</code> — social profile links.
            </li>
            <li>
              <code>connectSplash</code> — Join / Kit confirmation screens.
            </li>
            <li>
              <code>joinInvite</code> — homepage email invite popup.
            </li>
            <li>
              Set <code>active</code> to show or hide sections on the public
              site.
            </li>
          </ul>
        </div>

        <div className={styles["help-section"]}>
          <h3>Join / connect splash</h3>
          <ul>
            <li>
              Create one content item with type <code>connectSplash</code>. The
              active item drives both Join screens. If none is active, the site
              uses the built-in defaults.
            </li>
            <li>
              <strong>After they join</strong> is the “you&apos;re on the list”
              screen after Kit accepts the email (before they confirm).
            </li>
            <li>
              <strong>After they confirm</strong> is the thank-you at{" "}
              <code>/connected</code>. Point Kit&apos;s confirmation thank-you
              URL there (or <code>/?connected=1</code>).
            </li>
            <li>
              Leave a text field blank to keep the site default.
            </li>
            <li>
              Auto-continue seconds: <code>0</code> waits for the button; blank
              uses 7.
            </li>
            <li>
              <strong>Both screens</strong> has a help line above the button
              (default: email <code>connect@ignite-elysium.com</code> if they
              have trouble). Uncheck to hide it.
            </li>
          </ul>
        </div>

        <div className={styles["help-section"]}>
          <h3>Join invite popup</h3>
          <ul>
            <li>
              Create a content item with type <code>joinInvite</code>. The
              active item drives the homepage popup. If none is active, the
              default copy is shown.
            </li>
            <li>
              Uncheck <strong>Show the invite modal</strong> on an active item
              to hide the popup. Header, page, and footer Join stay available.
            </li>
            <li>
              It appears after the delay (default 12 seconds) or once someone
              scrolls halfway — whichever happens first. Close with the X, Esc,
              a click outside, or Not now.
            </li>
            <li>
              Closing it or joining hides it for 30 days (editable). Paste an
              E-0 cover URL, or leave it blank to look up a catalog item titled
              E-0.
            </li>
            <li>
              Preview with <code>/?joininvite=1</code>. Suppress with{" "}
              <code>/?joininvite=0</code>.
            </li>
          </ul>
        </div>

        <div className={styles["help-section"]}>
          <h3>Draft, publish, preview</h3>
          <ul>
            <li>
              <strong>Save draft</strong> stores unpublished copy on the same
              item. Visitors still see the live snapshot.
            </li>
            <li>
              <strong>Publish</strong> writes (or replaces) the published
              snapshot. That is not the same as going live on the page: the
              item still has to be <strong>Active</strong> for visitors to see
              it. Publish does not turn Active on or off.
            </li>
            <li>
              <strong>Active</strong> shows or hides the published snapshot on
              the public site. Checking it does not publish a draft.
            </li>
            <li>
              <strong>Revert to published</strong> throws away the unpublished
              draft (and unsaved edits) and restores the last published copy in
              the form. Visitors are not affected. Items that have never been
              published have no published copy to restore — delete them instead.
            </li>
            <li>
              <strong>Preview</strong> opens the real site with your unpublished
              copy and adds this item to a preview set. Visitors are unaffected.
              A yellow banner and Exit appear only in that admin preview tab.
            </li>
            <li>
              <strong>Include</strong> pins this item in the preview set without
              opening a tab. Preview then shows every pinned item together
              (Join Invite + Connect Splash + About, or several catalog
              tracks). A second Join Invite or Connect Splash replaces the
              previous one of that type. Catalog and content lists mark pinned
              items with an In preview badge. Unpin from Include or from the
              banner chips. Exit clears the set.
            </li>
            <li>
              <code>/?joininvite=1</code> without <code>preview=1</code> still
              previews the <strong>published</strong> Join Invite (legacy).
            </li>
            <li>
              Phone: log in as admin on that origin, Save draft, then open
              Preview. Unsaved-only preview stays in the same browser.
              Session cookie is about 43 minutes; JWT is 1 day. Overlay is not
              a credential. LAN host is not <code>localhost</code> (cookies are
              not shared).
            </li>
            <li>
              If Save draft is disabled, the backend does not yet know{" "}
              <code>draftPayload</code>. Use Publish until that deploy lands.
            </li>
          </ul>
        </div>

        <div className={styles["help-section"]}>
          <h3>Tracking</h3>
          <ul>
            <li>
              Tagged links use{" "}
              <code>
                utm_source, utm_medium, utm_campaign, utm_content
              </code>
              . The current default campaign is{" "}
              <code>{utmVocabulary.defaultCampaign}</code>. In the Tracking
              link builder, pick a known campaign or New campaign and type a
              slug (lowercase, underscores). That slug is written on the URL
              and recorded in Tracking. Add it to{" "}
              <code>utmVocabulary.json</code> if Grok Bot should offer it too.
              Email links use <code>utm_medium=email</code>; file / PDF links
              use <code>utm_medium=file</code>. Gab is{" "}
              <code>?utm_source=gab</code> only.
            </li>
            <li>
              The first tagged visit is saved in the browser as first-touch
              and counts as one landing. UTM tags are then stripped from the
              address bar so a bookmark is a clean URL. Later visits from that
              browser (bookmark, refresh in a new session, or the same tagged
              link) do not add landings. <code>/manage</code>, localhost, and
              CRA dev are not counted. Exclude this browser from the Dashboard
              or Tracking prompt so your public visits stay out of the numbers.
              Do that once per browser (laptop, phone). After Exclude, a prompt
              offers to remove the one visit already counted from this browser.
              Founding-list joins send first-touch values to Kit as custom
              fields.
            </li>
            <li>
              <Link to="/manage/tracking">Tracking</Link> shows anonymous
              landings and signups. Date range (7 / 30 / All, or From–To) and
              the source, medium, campaign, and content dropdowns sit under
              the stat cards. Headline stats, breakdowns, placements, and
              hits all follow that view. A placement is one UTM combination;
              First and Last are the earliest and most recent hits for that
              row. Hits lists each landing or signup with its date, path, and
              session. Correct counts is a quiet offset for a test visit or
              signup — recorded events stay, headline totals change (filters
              show recorded counts). Export JSON is the Helm / Grok Bot
              snapshot shape. The snapshot token lives in backend env{" "}
              <code>TRACK_SNAPSHOT_TOKEN</code>, not in this app.
            </li>
            <li>
              Create Kit custom fields named <code>utm_source</code>,{" "}
              <code>utm_medium</code>, <code>utm_campaign</code>,{" "}
              <code>utm_content</code>, and <code>first_touch_url</code>. Delete
              test subscribers after checking them.
            </li>
          </ul>
        </div>

        <div className={styles["help-section"]}>
          <h3>Keyboard</h3>
          <ul>
            <li>
              <code>⌘/Ctrl + S</code> — Save draft in the item editor.
            </li>
            <li>
              <code>Esc</code> — dismiss confirm dialogs.
            </li>
          </ul>
        </div>

        <div className={styles["help-section"]}>
          <h3>Need more?</h3>
          <p>
            Standalone reference page (same content, public URL):{" "}
            <Link to="/how-to-use">/how-to-use</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminHelp;
